/**
 * Campaign Zone Utilities
 * 
 * Handles multi-campaign zone spacing calculations during drag operations.
 * Used to maintain proper spacing between campaign zones when nodes are dragged.
 */
import { NODE_WIDTH, NODE_HEIGHT } from './canvasUtils';

// Constants for multi-campaign zone spacing (should match CampaignZones and multiCampaignLayout)
const ZONE_PADDING = 50;
const INTER_CAMPAIGN_GAP = 30;

/**
 * Calculate bounds for each campaign based on current positions
 * @param {Array} tasks - All tasks in the sprint
 * @param {Object} positions - Current positions of nodes
 * @param {Object} nodeDimensions - Dimensions of each node
 * @returns {{ bounds: Object, campaignOrder: Array }} Campaign bounds and order
 */
export function calculateCampaignBounds(tasks, positions, nodeDimensions) {
  const bounds = {};
  const campaignOrder = [];
  
  tasks.forEach(task => {
    const campaignId = task.sourceCampaign?.id;
    if (!campaignId) return;
    
    if (!bounds[campaignId]) {
      bounds[campaignId] = { minX: Infinity, maxX: -Infinity, minY: Infinity, maxY: -Infinity };
      campaignOrder.push(campaignId);
    }
    
    const pos = positions[task.id] || task.position || { x: 0, y: 0 };
    const dims = nodeDimensions[task.id] || { width: NODE_WIDTH, height: NODE_HEIGHT };
    
    bounds[campaignId].minX = Math.min(bounds[campaignId].minX, pos.x);
    bounds[campaignId].maxX = Math.max(bounds[campaignId].maxX, pos.x + dims.width);
    bounds[campaignId].minY = Math.min(bounds[campaignId].minY, pos.y);
    bounds[campaignId].maxY = Math.max(bounds[campaignId].maxY, pos.y + dims.height);
  });
  
  // Sort campaigns by their minX (left to right order)
  campaignOrder.sort((a, b) => bounds[a].minX - bounds[b].minX);
  
  return { bounds, campaignOrder };
}

/**
 * Calculate position adjustments for all campaigns based on zone spacing requirements
 * @param {Array} tasks - All tasks in the sprint
 * @param {Object} currentPositions - Current positions of all nodes
 * @param {Object} nodeDimensions - Dimensions of each node
 * @param {string} draggedCampaignId - ID of the campaign being dragged
 * @returns {Object} Adjustments per campaign ID
 */
export function calculateCampaignAdjustments(tasks, currentPositions, nodeDimensions, draggedCampaignId) {
  const { bounds, campaignOrder } = calculateCampaignBounds(tasks, currentPositions, nodeDimensions);
  
  if (campaignOrder.length <= 1) return {};
  
  const adjustments = {};
  campaignOrder.forEach(id => { adjustments[id] = 0; });
  
  // Find the index of the dragged campaign
  const draggedIndex = campaignOrder.indexOf(draggedCampaignId);
  if (draggedIndex === -1) return adjustments;
  
  // Check spacing to the LEFT of the dragged campaign
  for (let i = draggedIndex - 1; i >= 0; i--) {
    const rightCampaign = campaignOrder[i + 1];
    const leftCampaign = campaignOrder[i];
    
    const rightBounds = bounds[rightCampaign];
    const leftBounds = bounds[leftCampaign];
    
    // Calculate current gap between zones (accounting for zone padding)
    const rightZoneLeft = rightBounds.minX - ZONE_PADDING;
    const leftZoneRight = leftBounds.maxX + ZONE_PADDING;
    const currentGap = rightZoneLeft - leftZoneRight;
    
    if (currentGap < INTER_CAMPAIGN_GAP) {
      // Need to push left campaign further left
      const pushAmount = INTER_CAMPAIGN_GAP - currentGap;
      adjustments[leftCampaign] -= pushAmount;
      // Cascade to all campaigns further left
      for (let j = i - 1; j >= 0; j--) {
        adjustments[campaignOrder[j]] -= pushAmount;
      }
    } else if (currentGap > INTER_CAMPAIGN_GAP) {
      // Gap is too big, pull campaigns in
      const pullAmount = Math.min(currentGap - INTER_CAMPAIGN_GAP, currentGap - INTER_CAMPAIGN_GAP);
      adjustments[leftCampaign] += pullAmount;
      // Cascade to all campaigns further left
      for (let j = i - 1; j >= 0; j--) {
        adjustments[campaignOrder[j]] += pullAmount;
      }
    }
  }
  
  // Check spacing to the RIGHT of the dragged campaign
  for (let i = draggedIndex; i < campaignOrder.length - 1; i++) {
    const leftCampaign = campaignOrder[i];
    const rightCampaign = campaignOrder[i + 1];
    
    const leftBounds = bounds[leftCampaign];
    const rightBounds = bounds[rightCampaign];
    
    // Calculate current gap between zones (accounting for zone padding)
    const leftZoneRight = leftBounds.maxX + ZONE_PADDING;
    const rightZoneLeft = rightBounds.minX - ZONE_PADDING;
    const currentGap = rightZoneLeft - leftZoneRight;
    
    if (currentGap < INTER_CAMPAIGN_GAP) {
      // Need to push right campaign further right
      const pushAmount = INTER_CAMPAIGN_GAP - currentGap;
      adjustments[rightCampaign] += pushAmount;
      // Cascade to all campaigns further right
      for (let j = i + 2; j < campaignOrder.length; j++) {
        adjustments[campaignOrder[j]] += pushAmount;
      }
    } else if (currentGap > INTER_CAMPAIGN_GAP) {
      // Gap is too big, pull campaigns in
      const pullAmount = currentGap - INTER_CAMPAIGN_GAP;
      adjustments[rightCampaign] -= pullAmount;
      // Cascade to all campaigns further right
      for (let j = i + 2; j < campaignOrder.length; j++) {
        adjustments[campaignOrder[j]] -= pullAmount;
      }
    }
  }
  
  return adjustments;
}

/**
 * Apply campaign adjustments to positions during drag
 * @param {Array} tasks - All tasks
 * @param {Object} newPositions - Positions being updated (will be mutated)
 * @param {Object} currentPositions - All current positions
 * @param {Object} adjustments - Adjustments per campaign
 * @param {string} draggedCampaignId - Campaign being dragged (excluded from adjustments)
 */
export function applyCampaignAdjustments(tasks, newPositions, currentPositions, adjustments, draggedCampaignId) {
  tasks.forEach(task => {
    const campaignId = task.sourceCampaign?.id;
    if (campaignId && campaignId !== draggedCampaignId && adjustments[campaignId]) {
      const adjustment = adjustments[campaignId];
      if (Math.abs(adjustment) > 0.5) { // Only apply if adjustment is meaningful
        const currentPos = currentPositions[task.id];
        newPositions[task.id] = {
          x: currentPos.x + adjustment,
          y: currentPos.y,
        };
      }
    }
  });
}
